(() => {
  'use strict';

  const qsa = (root, selector) => Array.from((root || document).querySelectorAll(selector));
  const bindOnce = (element, key) => {
    const attribute = `data-nl-bound-${key}`;
    if (element.hasAttribute(attribute)) return false;
    element.setAttribute(attribute, 'true');
    return true;
  };
  const escapeSelector = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/([ #;?%&,.+*~\\':"!^$[\]()=>|/@])/g, '\\$1');
  };

  const fieldControls = (field) => qsa(field, '[data-nl-personalization-control]');

  const controlValue = (control, controls) => {
    if (!control) return '';
    if (control.type === 'radio') {
      const selected = controls.find((item) => item.type === 'radio' && item.checked && !item.disabled);
      return selected ? selected.value : '';
    }
    if (control.type === 'checkbox') return control.checked ? control.value || 'Taip' : '';
    if (control.type === 'file') return control.files && control.files.length ? control.files[0].name : '';
    return String(control.value || '').trim();
  };

  const fieldValue = (field) => {
    const controls = fieldControls(field);
    return controlValue(controls[0], controls);
  };

  const fieldIsNativeValid = (field) => fieldControls(field).every((control) => control.disabled || control.checkValidity());

  const clearFieldError = (field) => {
    field.classList.remove('has-error');
    const error = field.querySelector('[data-nl-field-error]');
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    fieldControls(field).forEach((control) => control.removeAttribute('aria-invalid'));
  };

  const setFieldError = (field, message) => {
    const label = field.dataset.nlLabel || 'šį lauką';
    field.classList.add('has-error');
    const error = field.querySelector('[data-nl-field-error]');
    if (error) {
      error.textContent = message || `Užpildykite lauką „${label}“.`;
      error.hidden = false;
    }
    fieldControls(field).forEach((control) => control.setAttribute('aria-invalid', 'true'));
  };

  const updateFieldState = (field) => {
    const value = fieldValue(field);
    field.classList.toggle('is-filled', Boolean(value));
    if (value && fieldIsNativeValid(field)) clearFieldError(field);

    const fileInput = field.querySelector('input[type="file"]');
    const fileName = field.querySelector('[data-nl-file-name]');
    if (fileInput && fileName) {
      fileName.textContent = fileInput.files && fileInput.files.length
        ? fileInput.files[0].name
        : 'Failas nepasirinktas';
    }
  };

  const updateCharacterCount = (control) => {
    if (!control.id) return;
    const counter = document.querySelector(`[data-nl-count-for="${escapeSelector(control.id)}"]`);
    if (!counter) return;
    const max = Number(control.getAttribute('maxlength')) || 0;
    const count = String(control.value || '').length;
    counter.textContent = max ? `${count} / ${max}` : String(count);
    counter.classList.toggle('is-near-limit', max > 0 && count >= Math.max(1, max - 5));
  };

  const updatePersonalizationGroup = (group) => {
    const fields = qsa(group, '[data-nl-personalization-field]');
    const requiredFields = fields.filter((field) => field.dataset.nlRequired === 'true');
    const completeRequired = requiredFields.filter((field) => Boolean(fieldValue(field))).length;
    const allComplete = requiredFields.length > 0 && completeRequired === requiredFields.length;
    const progress = group.querySelector('[data-nl-progress-bar]');
    const progressText = group.querySelector('[data-nl-progress-text]');
    const percent = requiredFields.length ? (completeRequired / requiredFields.length) * 100 : 100;

    group.classList.toggle('is-complete', allComplete);
    if (progress) progress.style.width = `${percent}%`;
    if (progressText) {
      progressText.textContent = allComplete
        ? 'Visi privalomi laukai užpildyti'
        : `Užpildyta ${completeRequired} iš ${requiredFields.length} privalomų laukų`;
      progressText.classList.toggle('is-complete', allComplete);
    }

    const summary = group.querySelector('[data-nl-summary]');
    const summaryList = group.querySelector('[data-nl-summary-list]');
    if (summary && summaryList) {
      summaryList.replaceChildren();
      let hasItems = false;
      fields.forEach((field) => {
        const value = fieldValue(field);
        if (!value) return;
        hasItems = true;
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');
        dt.textContent = field.dataset.nlLabel || '';
        dd.textContent = value;
        summaryList.append(dt, dd);
      });
      summary.hidden = !hasItems;
    }
  };

  const initPersonalization = (root = document) => {
    qsa(root, '[data-nl-personalization]').forEach((group) => {
      qsa(group, '[data-nl-personalization-field]').forEach((field) => {
        if (bindOnce(field, 'field')) {
          field.addEventListener('focusin', () => field.classList.add('has-focus'));
          field.addEventListener('focusout', () => field.classList.remove('has-focus'));
          fieldControls(field).forEach((control) => {
            const eventName = control.type === 'file'
              || control.type === 'radio'
              || control.type === 'checkbox'
              || control.tagName === 'SELECT'
              ? 'change'
              : 'input';
            control.addEventListener(eventName, () => {
              updateFieldState(field);
              updateCharacterCount(control);
              updatePersonalizationGroup(group);
            });
            control.addEventListener('invalid', (event) => {
              event.preventDefault();
              setFieldError(field);
            });
            updateCharacterCount(control);
          });
        }
        updateFieldState(field);
      });
      updatePersonalizationGroup(group);
    });
  };

  const validatePersonalization = (form) => {
    const group = form.querySelector('[data-nl-personalization]');
    if (!group) return true;

    const fields = qsa(group, '[data-nl-personalization-field]');
    let firstInvalid = null;
    fields.forEach((field) => {
      const required = field.dataset.nlRequired === 'true';
      const complete = Boolean(fieldValue(field));
      const nativeValid = fieldIsNativeValid(field);
      if (required && !complete) {
        setFieldError(field);
        if (!firstInvalid) firstInvalid = field;
      } else if (!nativeValid) {
        setFieldError(field, `Patikrinkite lauką „${field.dataset.nlLabel || 'Personalizacija'}“ bei įvestą reikšmę.`);
        if (!firstInvalid) firstInvalid = field;
      } else {
        clearFieldError(field);
      }
    });
    updatePersonalizationGroup(group);

    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => {
        const focusTarget = fieldControls(firstInvalid).find((control) => !control.disabled);
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }, 350);
      return false;
    }
    return true;
  };

  window.NLPersonalization = { validate: validatePersonalization, refresh: initPersonalization };

  const initInfoTabs = (root = document) => {
    qsa(root, '[data-noru-info-tabs]').forEach((tabsRoot) => {
      const buttons = qsa(tabsRoot, '[data-noru-tab]');
      const panels = qsa(tabsRoot, '[data-noru-panel]');
      const activate = (button, moveFocus = false) => {
        const target = button.dataset.noruTab;
        buttons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
          item.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => {
          const active = panel.dataset.noruPanel === target;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });
        if (moveFocus) button.focus();
      };

      buttons.forEach((button, index) => {
        if (!bindOnce(button, 'info-tab')) return;
        button.tabIndex = index === 0 ? 0 : -1;
        button.addEventListener('click', () => activate(button));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const current = buttons.indexOf(button);
          let next = current;
          if (event.key === 'ArrowRight') next = (current + 1) % buttons.length;
          if (event.key === 'ArrowLeft') next = (current - 1 + buttons.length) % buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = buttons.length - 1;
          activate(buttons[next], true);
        });
      });
    });
  };

  const initProductFilters = (root = document) => {
    qsa(root, '[data-nl-product-section]').forEach((section) => {
      const tabs = qsa(section, '[data-nl-filter]');
      const cards = qsa(section, '[data-nl-product-card]');
      const noMatch = section.querySelector('[data-nl-no-match]');
      const activate = (tab, moveFocus = false) => {
        const keywords = String(tab.dataset.nlKeywords || '')
          .toLocaleLowerCase('lt-LT')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
        let visibleCount = 0;

        tabs.forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
          item.tabIndex = active ? 0 : -1;
        });
        cards.forEach((card) => {
          const haystack = String(card.dataset.productSearch || '').toLocaleLowerCase('lt-LT');
          const visible = tab.dataset.nlFilter === 'all' || keywords.some((keyword) => haystack.includes(keyword));
          card.hidden = !visible;
          if (visible) visibleCount += 1;
        });
        if (noMatch) noMatch.hidden = visibleCount > 0;
        if (moveFocus) tab.focus();
      };

      tabs.forEach((tab, index) => {
        if (!bindOnce(tab, 'product-filter')) return;
        tab.tabIndex = index === 0 ? 0 : -1;
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const current = tabs.indexOf(tab);
          let next = current;
          if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          activate(tabs[next], true);
        });
      });
    });
  };

  const refreshVariantGroup = (group) => {
    if (!group) return;
    const output = group.querySelector('[data-nl-variant-selected]');
    const radios = qsa(group, 'input[type="radio"]');
    const select = group.querySelector('[data-nl-variant-select]');
    let value = '';

    if (select) value = select.options[select.selectedIndex]?.textContent?.trim() || select.value || '';
    if (radios.length) {
      const checked = radios.find((radio) => radio.checked);
      value = checked ? checked.value : '';
    }

    if (output) output.textContent = value;
    group.classList.toggle('has-selection', Boolean(value));
  };

  const initVariantGroups = (root = document) => {
    qsa(root, '[data-nl-variant-group]').forEach(refreshVariantGroup);

    if (bindOnce(document.documentElement, 'variant-delegation')) {
      document.addEventListener('change', (event) => {
        const group = event.target.closest?.('[data-nl-variant-group]');
        if (group) refreshVariantGroup(group);
      });
    }
  };

  const initGenericFields = (root = document) => {
    const forms = new Set();
    qsa(root, '[data-nl-field-wrap]').forEach((wrap) => {
      const control = wrap.querySelector('[data-nl-control]');
      if (!control) return;
      if (control.form) forms.add(control.form);

      const refresh = () => {
        const value = control.type === 'checkbox' ? control.checked : String(control.value || '').trim();
        wrap.classList.toggle('is-filled', Boolean(value));
        if (control.validity && control.validity.valid) {
          wrap.classList.remove('has-error');
          control.removeAttribute('aria-invalid');
        }
        updateCharacterCount(control);
      };

      if (bindOnce(control, 'generic-field')) {
        control.addEventListener('focus', () => wrap.classList.add('has-focus'));
        control.addEventListener('blur', () => {
          wrap.classList.remove('has-focus');
          if (control.required && !control.validity.valid) {
            wrap.classList.add('has-error');
            control.setAttribute('aria-invalid', 'true');
          }
        });
        control.addEventListener(control.tagName === 'SELECT' || control.type === 'checkbox' ? 'change' : 'input', refresh);
        control.addEventListener('invalid', () => {
          wrap.classList.add('has-error');
          control.setAttribute('aria-invalid', 'true');
        });
      }
      refresh();
    });

    forms.forEach((form) => {
      if (!bindOnce(form, 'generic-form')) return;
      form.addEventListener('submit', () => {
        qsa(form, '[data-nl-control]').forEach((control) => {
          const wrap = control.closest('[data-nl-field-wrap]');
          if (!wrap) return;
          const invalid = !control.validity.valid;
          wrap.classList.toggle('has-error', invalid);
          if (invalid) control.setAttribute('aria-invalid', 'true');
        });
      });
    });
  };

  const initNav = (root = document) => {
    const current = `${window.location.pathname}${window.location.search}`;
    qsa(root, '[data-nl-nav-link]').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        const target = `${url.pathname}${url.search}`;
        const active = target === current || (url.pathname === window.location.pathname && url.pathname !== '/');
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      } catch (_) {
        // Ignore malformed merchant-entered URLs.
      }
    });
  };

  const initAll = (root = document) => {
    initPersonalization(root);
    initInfoTabs(root);
    initProductFilters(root);
    initVariantGroups(root);
    initGenericFields(root);
    initNav(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
